import type { Transition, Variants } from 'framer-motion';

/**
 * Global animation configuration
 * Matching existing CSS variables from index.css for consistency
 */

// Easing curves matching --mq-motion-ease: cubic-bezier(0.22, 1, 0.36, 1)
export const easings = {
  smooth: [0.22, 1, 0.36, 1] as const,
  easeOut: [0, 0, 0.2, 1] as const,
  easeIn: [0.4, 0, 1, 1] as const,
  spring: { type: 'spring' as const, stiffness: 300, damping: 30 },
  bounce: { type: 'spring' as const, stiffness: 400, damping: 10 },
};

// Duration scale - can be adjusted globally
export const durations = {
  instant: 0,
  fast: 0.15,
  normal: 0.3,
  slow: 0.5,
  slower: 0.65, // Matches --mq-motion-duration: 650ms
};

// Default transition matching existing CSS
export const defaultTransition: Transition = {
  duration: durations.slow,
  ease: easings.smooth,
};

// Page transition variants
export const pageTransitionVariants: Variants = {
  initial: {
    opacity: 0,
    y: 20,
    scale: 0.98,
  },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
  },
  exit: {
    opacity: 0,
    y: -20,
    scale: 1.02,
  },
};

// Fade variants
export const fadeVariants: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

// Slide variants (direction-aware for RTL support)
export const getSlideVariants = (isRTL: boolean): Variants => ({
  initial: {
    opacity: 0,
    x: isRTL ? 30 : -30,
  },
  animate: {
    opacity: 1,
    x: 0,
  },
  exit: {
    opacity: 0,
    x: isRTL ? -30 : 30,
  },
});

// Scale variants for cards/modals
export const scaleVariants: Variants = {
  initial: {
    opacity: 0,
    scale: 0.9,
  },
  animate: {
    opacity: 1,
    scale: 1,
  },
  exit: {
    opacity: 0,
    scale: 0.9,
  },
};

// Stagger container variants
export const staggerContainerVariants: Variants = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1,
    },
  },
  exit: {
    transition: {
      staggerChildren: 0.03,
      staggerDirection: -1,
    },
  },
};

// Stagger item variants
export const staggerItemVariants: Variants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: defaultTransition,
  },
  exit: {
    opacity: 0,
    y: -10,
  },
};

// Scroll reveal variants
export const scrollRevealVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 40,
    scale: 0.98,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: durations.slower,
      ease: easings.smooth,
    },
  },
};

// Hover/tap interaction variants for cards
export const cardInteractionVariants: Variants = {
  rest: {
    scale: 1,
    y: 0,
  },
  hover: {
    scale: 1.02,
    y: -4,
    transition: {
      duration: durations.fast,
      ease: easings.easeOut,
    },
  },
  tap: {
    scale: 0.98,
  },
};

// Button interaction variants
export const buttonInteractionVariants: Variants = {
  rest: { scale: 1 },
  hover: {
    scale: 1.05,
    transition: {
      duration: durations.fast,
      ease: easings.easeOut,
    },
  },
  tap: {
    scale: 0.95,
    transition: {
      duration: durations.instant,
    },
  },
};

// Modal/overlay variants
export const overlayVariants: Variants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: durations.normal },
  },
  exit: {
    opacity: 0,
    transition: { duration: durations.fast },
  },
};

export const modalVariants: Variants = {
  initial: {
    opacity: 0,
    scale: 0.95,
    y: 20,
  },
  animate: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: durations.normal,
      ease: easings.smooth,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: {
      duration: durations.fast,
    },
  },
};

// Get reduced motion safe variants
export const getReducedMotionVariants = (
  variants: Variants,
  prefersReducedMotion: boolean
): Variants => {
  if (!prefersReducedMotion) return variants;

  // Return instant transitions for reduced motion
  const reducedVariants: Variants = {};
  for (const key in variants) {
    const variant = variants[key];
    if (typeof variant === 'object' && variant !== null) {
      reducedVariants[key] = {
        ...variant,
        transition: { duration: 0 },
      };
    } else {
      reducedVariants[key] = variant;
    }
  }
  return reducedVariants;
};

// Animation config type for components
export interface AnimationConfig {
  enabled: boolean;
  reducedMotion: boolean;
  duration: number;
  ease: readonly number[] | { type: string; stiffness: number; damping: number };
}

// Default animation config
export const defaultAnimationConfig: AnimationConfig = {
  enabled: true,
  reducedMotion: false,
  duration: durations.slow,
  ease: easings.smooth,
};

// Mobile-optimized durations (shorter for better perceived performance)
export const mobileDurations = {
  instant: 0,
  fast: 0.1,
  normal: 0.2,
  slow: 0.35,
  slower: 0.45,
};

// Mobile-optimized animation distances (smaller movements)
export const mobileDistances = {
  small: 10,
  medium: 20,
  large: 30,
};

// Desktop animation distances
export const desktopDistances = {
  small: 20,
  medium: 40,
  large: 60,
};

// Get device-appropriate durations
export const getOptimizedDurations = (isMobile: boolean) =>
  isMobile ? mobileDurations : durations;

// Get device-appropriate distances
export const getOptimizedDistances = (isMobile: boolean) =>
  isMobile ? mobileDistances : desktopDistances;

// RTL-aware slide variants with mobile optimization
export const getOptimizedSlideVariants = (
  isRTL: boolean,
  isMobile: boolean
): Variants => {
  const distance = isMobile ? mobileDistances.medium : desktopDistances.medium;
  const duration = isMobile ? mobileDurations.normal : durations.normal;

  return {
    initial: {
      opacity: 0,
      x: isRTL ? distance : -distance,
    },
    animate: {
      opacity: 1,
      x: 0,
      transition: {
        duration,
        ease: easings.smooth,
      },
    },
    exit: {
      opacity: 0,
      x: isRTL ? -distance : distance,
      transition: {
        duration: duration * 0.8,
        ease: easings.easeIn,
      },
    },
  };
};

// Mobile-optimized stagger config
export const getMobileStaggerConfig = (isMobile: boolean) => ({
  staggerChildren: isMobile ? 0.03 : 0.05,
  delayChildren: isMobile ? 0.05 : 0.1,
});

// Touch-friendly interaction variants (larger scale changes for visibility)
export const touchInteractionVariants: Variants = {
  rest: {
    scale: 1,
  },
  tap: {
    scale: 0.95,
    transition: {
      duration: 0.1,
    },
  },
};

// Desktop hover interaction variants
export const hoverInteractionVariants: Variants = {
  rest: {
    scale: 1,
    y: 0,
  },
  hover: {
    scale: 1.02,
    y: -4,
    transition: {
      duration: durations.fast,
      ease: easings.easeOut,
    },
  },
  tap: {
    scale: 0.98,
  },
};

// Get appropriate interaction variants based on device
export const getInteractionVariants = (isTouchDevice: boolean): Variants =>
  isTouchDevice ? touchInteractionVariants : hoverInteractionVariants;
